import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../helpers/helpers";
import { useParams } from "react-router-dom";
import { safe_get } from "../../report-visuals/report-utils";

import { OverallSummaryCard } from "./overall-summary-card";
import { BoothGrid } from "./booth-grid";
import { PivGrid } from "./piv-grid";
import { SectionCard } from "./section-card";
import { Tabs } from "./tab-components";
import { LoadingState } from "../../components/LoadingState";
import { FTPSearchBar } from "../../components-new/FTPSearchBar";

type AccordionTitleProps = {
    title: string;
    isOpen: boolean;
    onClickHandler: Function;
};

export const AccordionTitle = (props: AccordionTitleProps) => {
    const { title, onClickHandler, isOpen } = props;

    return (
        <div
            onClick={() => onClickHandler(!isOpen)}
            className="mt-2 flex flex-row items-center rounded-lg bg-white shadow-lg"
        >
            <div className="m-4 items-center font-bold text-purple-600">{title}</div>
            <div className="ml-auto p-4">
                {isOpen ? (
                    <i className="fa-sharp fa-solid fa-chevron-up" />
                ) : (
                    <i className="fa-solid fa-chevron-down" />
                )}
            </div>
        </div>
    );
};

const ElectionDayDashboard = observer(() => {
    const { electionDayDashboardStore, projectStore } = useStore();
    const { projectid } = useParams();

    const [activeTab, setActiveTab] = useState("booth");
    const [searchQuery, setSearchQuery] = useState("");
    const [ourParty, setOurParty] = useState("");

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        setSearchQuery("");
    }, [activeTab]);

    const load = async () => {
        await electionDayDashboardStore.getElectionDayDashboardData(projectid);

        const gotvconfig = projectStore.getProjectProperty("gotv_config");
        setOurParty(safe_get(gotvconfig, "our_party", ""));
    };

    // if (electionDayDashboardStore.isLoading) return <LoadingState />;

    const data = electionDayDashboardStore.electionDayDashboardData;

    const filterData = (dataObj: any, query: string) => {
        if (!query) return dataObj;

        const lower = query.toLowerCase();

        return Object.fromEntries(
            Object.entries(dataObj || {}).filter(([key, val]: any) => {
                if (String(key).toLowerCase().includes(lower)) return true;

                if (typeof val?.section === "string" && val.section.toLowerCase().includes(lower))
                    return true;

                if (typeof val?.piv_level_1 === "string" && val.piv_level_1.toLowerCase().includes(lower))
                    return true;

                if (typeof val?.part === "string" && val.part.toLowerCase().includes(lower)) return true;

                if (
                    Array.isArray(val?.part) &&
                    val.part.some((p: any) => String(p).toLowerCase().includes(lower))
                )
                    return true;

                return false;
            })
        );
    };

    const filteredBooth = filterData(data?.booth_wise, searchQuery);

    const filteredPiv = filterData(data?.piv_level1_wise, searchQuery);
    const filteredSection = filterData(data?.section, searchQuery);

    return (
        <div className="min-h-screen space-y-6 bg-gray-50 p-6">
            <OverallSummaryCard data={data?.ac_wise} />

            <div className="flex items-center justify-between">
                <FTPSearchBar
                    placeHolder={`Search ${activeTab}...`}
                    clientSideSearch={true}
                    onSearch={(val) => setSearchQuery(val)}
                />
            </div>

            <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />

            {activeTab === "booth" && (
                <>
                    {Object.keys(filteredBooth || {}).length === 0 ? (
                        <div className="mt-6 text-center text-gray-400">No booths found</div>
                    ) : (
                        <BoothGrid data={filteredBooth} />
                    )}
                </>
            )}

            {activeTab === "piv" && (
                <>
                    {Object.keys(filteredPiv || {}).length === 0 ? (
                        <div className="mt-6 text-center text-gray-400">No piv data found</div>
                    ) : (
                        <PivGrid data={filteredPiv} />
                    )}
                </>
            )}

            {activeTab === "section" && (
                <div className="mt-4">
                    <div className="mb-3 flex justify-between">
                        <h3 className="text-sm font-semibold text-gray-700">Street / Section Performance</h3>

                        <span className="text-xs text-gray-400">
                            {Object.keys(filteredSection || {}).length} sections
                        </span>
                    </div>

                    {Object.keys(filteredSection || {}).length === 0 ? (
                        <div className="mt-6 text-center text-gray-400">No sections found</div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                            {Object.entries(filteredSection || {})
                                .sort(([a], [b]) =>
                                    a.localeCompare(b, undefined, {
                                        numeric: true,
                                        sensitivity: "base",
                                    })
                                )
                                .map(([key, val]: any) => (
                                    <SectionCard key={key} name={key} data={val} />
                                ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

export default ElectionDayDashboard;
